// Card print-variant derivation — which variants of a card actually exist
// and can be logged (Regular / Holo / Reverse Holo / pattern reverses).
//
// This used to be hand-written per-set rules, which was wrong in both
// directions (missing Black Bolt's ball patterns, offering Pokeball on
// Prismatic exes that never had it, giving Paldean Fates shinies a phantom
// second variant). Variants are per-CARD facts, so the engine now derives
// them from data, in a 3-layer ladder:
//
//   1. printVariants map (src/data/printVariants.js, generated from tcgdex
//      variants_detailed by scripts/build-variant-maps.mjs) — the only
//      source that knows pattern reverses, and the base-structure source
//      for sets with no price data (the 2026 ME era).
//   2. TCGPlayer price keys embedded in the card JSON (card.tcgplayer.prices)
//      — a card that only sells as "holofoil" IS holo-only. Covers ~95% of
//      the catalog. Consulted only for cards WITHOUT a map entry: the map is
//      booster-print-curated (product prints like tinsel holos excluded and
//      tcgdex's occasional bogus `normal` clamped at build time), while
//      TCGPlayer lumps product prints into its generic holofoil SKU — so for
//      mapped cards the map wins wholesale.
//   3. Rarity rules — last-resort fallback for cards with neither source
//      (McDonald's promos, trainer kits, brand-new sets pre-indexing).
//
// `fixedVariantsByRarity` (Ace Spec → Holo-only, BUG-001) clamps before
// everything; SET_VARIANT_RULES.specialVariants remains as a manual additive
// override for future data gaps, currently unused.
//
// Naming note: "Regular" strictly means the NON-holo standard print. Cards
// whose standard print is holofoil (SV/ME-era Rares and up, shinies, secret
// rares) are "Holo" — one card, one truthful label per real print.

import printVariantData from '../data/printVariants.js';

const PRINT_VARIANT_SETS = printVariantData.sets;

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

// Set-specific overrides. The data ladder makes per-set rules unnecessary for
// anything it covers — add entries here only to patch a data gap.
//   allCards: [variants] — absolute clamp for EVERY card in the set, wins
//     over everything below (rarity clamps, map, prices, rules). For products
//     that are single-print by construction.
//   fixedVariantsByRarity: { <rarity>: [variants] } — absolute clamp, wins
//     over every data source (mep promos are holo-only; tcgdex agrees, this
//     guards against a bad regeneration).
//   specialVariants: scoped descriptors ({ name, rarities?, excludeRarities?,
//     minNumber?, maxNumber? } or plain strings) — additive, applied when a
//     card has no map entry.
const SET_VARIANT_RULES = {
  // 30th Celebration + its Classic Collection subset (BUG-010): every card is
  // a single foil print. Clamped set-wide instead of left to the data ladder:
  // a stray `normal` price key or a rules fallback would invent a Regular
  // print that doesn't exist, and Classic Collection cards keep original-set
  // numbers (three share #106), so a number-keyed map entry would be
  // ambiguous anyway.
  'me55': { allCards: ['Holo'] },
  'me55c': { allCards: ['Holo'] },
  'mep': {
    fixedVariantsByRarity: { 'Promo': ['Holo'] }
  },
};

// Rarities that get a fixed variant list regardless of set or data source.
// Ace Spec cards are only ever printed as Holo (BUG-001).
const FIXED_RARITY_VARIANTS = {
  'ACE SPEC Rare': ['Holo'], // Scarlet & Violet era
  'Rare ACE': ['Holo'],      // Black & White era
};

// TCGPlayer price key -> app variant name. 1st edition / unlimited keys fold
// into their base finish — the app doesn't model edition as a variant.
const PRICE_KEY_TO_VARIANT = {
  normal: 'Regular',
  unlimited: 'Regular',
  '1stEdition': 'Regular',
  holofoil: 'Holo',
  unlimitedHolofoil: 'Holo',
  '1stEditionHolofoil': 'Holo',
  reverseHolofoil: 'Reverse Holo',
};

const BASE_TRIO = ['Regular', 'Holo', 'Reverse Holo'];

// Fallback-layer rarity classification (only consulted when a card has no
// map entry and no price data). Substring match, case-sensitive to match
// API rarity strings.
const HOLO_ONLY_RARITY_HINTS = [
  'Illustration Rare', 'Special Illustration', 'Hyper Rare', 'Ultra Rare',
  'Secret Rare', 'Shiny', 'Radiant', 'Rainbow', 'Gold', 'Double Rare',
  'Rare Holo', 'Mega', 'Black White Rare', 'Prism',
];

// Card name patterns that never had reverse-holo prints (older eras; modern
// equivalents are all holo-only rarities anyway).
const NO_REVERSE_NAME_PATTERNS = [' ex', ' EX', ' GX', ' V ', ' VMAX', ' VSTAR', ' VStar', 'Radiant '];

// Series where the plain "Rare" tier is holofoil-standard.
const HOLO_RARE_SERIES = ['Scarlet & Violet', 'Mega Evolution'];

// Reverse holos have existed since Legendary Collection (2002/05) — but a few
// products since then genuinely have none. Used both to gate the reverse
// backfill below and to suppress false anomalies in the audit.
export const NO_REVERSE_SETS = new Set([
  'pop1', 'pop2', 'pop3', 'pop4', 'pop5', 'pop6', 'pop7', 'pop8', 'pop9',
  'det1',   // Detective Pikachu
  'np',     // Nintendo Black Star Promos
  'basep',  // Wizards Black Star Promos
  'bp',     // Best of Game
  'si1',    // Southern Islands
]);
const REVERSE_ERA_START = '2002/05/01';

// Display configuration per variant.
const VARIANT_CONFIG = {
  'Regular':        { color: 'bg-blue-500',    short: 'R',  priority: 1,  desc: 'Standard non-holo print' },
  'Holo':           { color: 'bg-yellow-500',  short: 'H',  priority: 2,  desc: 'Holofoil print' },
  'Reverse Holo':   { color: 'bg-purple-500',  short: 'RH', priority: 3,  desc: 'Reverse holographic pattern' },
  'Poke Ball':      { color: 'bg-red-500',     short: 'PB', priority: 4,  desc: 'Poké Ball pattern reverse' },
  'Master Ball':    { color: 'bg-purple-800',  short: 'MB', priority: 5,  desc: 'Master Ball pattern reverse' },
  'Friend Ball':    { color: 'bg-green-600',   short: 'FB', priority: 6,  desc: 'Friend Ball pattern reverse' },
  'Love Ball':      { color: 'bg-pink-500',    short: 'LB', priority: 7,  desc: 'Love Ball pattern reverse' },
  'Quick Ball':     { color: 'bg-sky-500',     short: 'QB', priority: 8,  desc: 'Quick Ball pattern reverse' },
  'Dusk Ball':      { color: 'bg-teal-800',    short: 'DB', priority: 9,  desc: 'Dusk Ball pattern reverse' },
  'Team Rocket':    { color: 'bg-zinc-800',    short: 'TR', priority: 10, desc: 'Team Rocket pattern reverse' },
  // orange, not amber: the Tailwind theme overrides `amber` with a flat brand
  // string, which deletes the default amber shade scale (bg-amber-600 = no CSS).
  'Energy Pattern': { color: 'bg-orange-500',  short: 'EP', priority: 11, desc: 'Energy symbol pattern reverse' },
  'League Pattern': { color: 'bg-indigo-600',  short: 'LP', priority: 12, desc: 'League pattern reverse' },
};

// ---------------------------------------------------------------------------
// Derivation ladder
// ---------------------------------------------------------------------------

// Layer 1: per-card map entry, or null.
const mapEntryFor = (setId, cardNumber) => {
  const bySet = PRINT_VARIANT_SETS[setId];
  if (!bySet) return null;
  const key = String(parseInt(cardNumber, 10));
  return key === 'NaN' ? null : bySet[key] || null;
};

// Layer 2: base trio from TCGPlayer price keys, or null when no price data.
const baseTrioFromPrices = (card) => {
  const prices = card.tcgplayer?.prices;
  if (!prices) return null;
  const keys = Object.keys(prices);
  if (keys.length === 0) return null;
  const base = [];
  for (const key of keys) {
    const name = PRICE_KEY_TO_VARIANT[key];
    if (name && !base.includes(name)) base.push(name);
  }
  return base.length ? base : null;
};

// Layer 3: rarity/era rules for cards with no data at all.
const baseTrioFromRules = (card, cardNumber, setTotal) => {
  const rarity = card.rarity || '';
  const name = card.name || '';

  const isSecret = !isNaN(cardNumber) && cardNumber > setTotal;
  if (isSecret) return ['Holo'];
  if (HOLO_ONLY_RARITY_HINTS.some((h) => rarity.includes(h))) return ['Holo'];
  if (rarity === 'Rare' && HOLO_RARE_SERIES.includes(card.set?.series)) {
    return ['Holo', 'Reverse Holo'];
  }

  const variants = ['Regular'];
  const noReverse = NO_REVERSE_NAME_PATTERNS.some((p) => name.includes(p));
  if (!noReverse) variants.push('Reverse Holo');
  return variants;
};

// Normalize a specialVariants entry to descriptor form (plain strings are
// shorthand for "applies to every card in the set").
const toVariantDescriptor = (entry) =>
  typeof entry === 'string' ? { name: entry } : entry;

const variantAppliesToCard = (descriptor, cardRarity, cardNumber) => {
  const { rarities, excludeRarities, minNumber, maxNumber } = descriptor;
  if (rarities && !rarities.includes(cardRarity)) return false;
  if (excludeRarities && excludeRarities.includes(cardRarity)) return false;
  if (minNumber !== undefined || maxNumber !== undefined) {
    if (isNaN(cardNumber)) return false;
    if (minNumber !== undefined && cardNumber < minNumber) return false;
    if (maxNumber !== undefined && cardNumber > maxNumber) return false;
  }
  return true;
};

/**
 * The variants of a card that actually exist as booster prints.
 * @param {Object} card - card object (API shape; needs number/rarity/set,
 *   uses tcgplayer.prices when present)
 * @returns {string[]} variant names, display-priority order
 */
export const getCardVariants = (card) => {
  if (!card) return ['Regular'];

  const setId = card.set?.id;
  const cardNumber = parseInt(card.number, 10);
  const setTotal = parseInt(card.set?.printedTotal || card.set?.total || 999, 10);
  const cardRarity = card.rarity || '';
  const setRules = SET_VARIANT_RULES[setId];

  // Absolute clamps first (single-print sets, Ace Spec, mep promos).
  if (setRules?.allCards) return [...setRules.allCards];
  const fixed = setRules?.fixedVariantsByRarity?.[cardRarity]
    || FIXED_RARITY_VARIANTS[cardRarity];
  if (fixed) return [...fixed];

  const mapEntry = mapEntryFor(setId, card.number);

  let variants;
  if (mapEntry) {
    // Map wins wholesale — it's booster-curated (see header).
    variants = [...mapEntry];
  } else {
    const priceTrio = baseTrioFromPrices(card);
    variants = priceTrio || baseTrioFromRules(card, cardNumber, setTotal);

    // Reverse-era backfill: TCGPlayer often has no reverse-holo SKU for cheap
    // old commons even though the print exists (every standard card since
    // Legendary Collection has one). If prices told us the card has a normal
    // print but no reverse, and the set/tier should have reverses, add it.
    if (
      priceTrio &&
      variants.includes('Regular') &&
      !variants.includes('Reverse Holo') &&
      ['Common', 'Uncommon', 'Rare'].includes(cardRarity) &&
      !NO_REVERSE_SETS.has(setId) &&
      (card.set?.releaseDate || '') >= REVERSE_ERA_START &&
      !(cardNumber > setTotal)
    ) {
      variants.push('Reverse Holo');
    }
  }

  // Manual additive overrides (data-gap escape hatch; only for unmapped cards
  // so a stale rule can never double-stack on top of real data).
  if (!mapEntry && setRules?.specialVariants) {
    setRules.specialVariants
      .map(toVariantDescriptor)
      .forEach((descriptor) => {
        if (!variantAppliesToCard(descriptor, cardRarity, cardNumber)) return;
        if (!variants.includes(descriptor.name)) variants.push(descriptor.name);
      });
  }

  return sortVariantsByPriority([...new Set(variants)]);
};

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export const getVariantConfig = (variant) => {
  return VARIANT_CONFIG[variant] || {
    color: 'bg-gray-500',
    short: variant.charAt(0),
    priority: 999
  };
};

export const getVariantColor = (variant) => getVariantConfig(variant).color;
export const getVariantShort = (variant) => getVariantConfig(variant).short;
export const getVariantPriority = (variant) => getVariantConfig(variant).priority;

export const sortVariantsByPriority = (variants) => {
  return variants.sort((a, b) => getVariantPriority(a) - getVariantPriority(b));
};

/**
 * Check if a card is considered a "hit" (valuable pull)
 */
export const isHitCard = (card, hitRarities) => {
  return hitRarities.some(rarity => card.rarity?.includes(rarity));
};

/**
 * Every variant that appears anywhere in a set (map-aware; sets without a
 * map report the standard trio).
 */
export const getSetVariants = (setId) => {
  if (SET_VARIANT_RULES[setId]?.allCards) return [...SET_VARIANT_RULES[setId].allCards];
  const bySet = PRINT_VARIANT_SETS[setId];
  if (!bySet) return [...BASE_TRIO];
  const all = new Set();
  for (const names of Object.values(bySet)) for (const n of names) all.add(n);
  return sortVariantsByPriority([...all]);
};

export const hasSetVariantRules = (setId) =>
  Boolean(SET_VARIANT_RULES[setId] || PRINT_VARIANT_SETS[setId]);

export const getSetVariantStats = (setId) => {
  const variants = getSetVariants(setId);
  const special = variants.filter((v) => !BASE_TRIO.includes(v));
  return {
    hasSpecialVariants: special.length > 0,
    specialVariants: special,
    totalVariantTypes: variants.length
  };
};

// Export the set rules for debugging/admin purposes
export const getSetVariantRules = () => SET_VARIANT_RULES;
